import { resetDirty } from "@frui.ts/dirtycheck";
import { bound } from "@frui.ts/helpers";
import { Router } from "@frui.ts/screens";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { attachAutomaticValidator, hideValidationErrors, validate } from "@frui.ts/validation";
import { Modules } from "config";
import download from "downloadjs";
import Distributor from "entities/distributor";
import DistributorIssuedPaymentRequestResponse from "entities/distributorIssuedPaymentRequestResponse";
import DistributorsPaymentRequestStatus from "entities/distributorPaymentRequestStatus";
import type DistributorPaymentRequestType from "entities/distributorPaymentRequestType";
import DistributorPaymentRequestFilter, {
  DistributorPaymentRequestFilterValidationRules,
} from "entities/filters/distributorPaymentRequestFilter";
import { computed, observable, when } from "mobx";
import DistributorRepository from "repositories/distributorRepository";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import OptionsService from "services/optionsService";
import SecurityService from "services/securityService";
import { watchBusy } from "utils/busyWatcher";
import emptyListResponse from "utils/emptyListResponse";
import { isFilterEmpty, serializeFilters } from "utils/filters";
import type { ISelectItem } from "../../../../types";
import ListViewModel from "../../../listViewModel";

const navigationName = "payment-request-reports";

@Router.registerRoute({ route: navigationName, name: Router.Self })
export default class DistributorPaymentRequestReportsViewModel extends ListViewModel<
  DistributorIssuedPaymentRequestResponse,
  ScreenBase,
  ScreenBase,
  DistributorPaymentRequestFilter
> {
  readonly modelName = "distributorIssuedPaymentRequest";
  @observable private distributors: ISelectItem[] = [];

  constructor(
    protected repository: DistributorRepository,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected notificationService: NotificationService,
    private optionsService: OptionsService
  ) {
    super();
    this.navigationName = navigationName;
  }

  @computed
  get allowDelete(): boolean {
    return false;
  }

  @computed
  get distributorId() {
    return this.appliedFilter?.distributorId;
  }

  @watchBusy
  async onInitialize(): Promise<void> {
    try {
      await when(() => this.optionsService.optionsViewModel(Distributor).isLoaded);
      this.distributors = this.optionsService.entityOptions(Distributor);
      if (this.distributors.length === 0) {
        this.notificationService.add({ title: this.translate("distributorsEmpty"), type: NotificationType.ERROR });
        return;
      }
    } catch (e) {
      console.error(e);
      return;
    }
    super.onInitialize();
  }

  protected fetchData() {
    if (this.distributorId) {
      const { offset, limit } = this.pagingFilter;
      return this.repository.distributorPaymentRequests(this.distributorId, { offset, limit }, this.appliedFilter);
    }

    return emptyListResponse();
  }

  protected createFilter(): DistributorPaymentRequestFilter {
    return attachAutomaticValidator(new DistributorPaymentRequestFilter(), DistributorPaymentRequestFilterValidationRules);
  }

  protected resetFilterValues(filter: DistributorPaymentRequestFilter) {
    super.resetFilterValues(filter);
    hideValidationErrors(this.filter);
  }

  // Method overload to prevent validate filters when are empty
  applyFilter() {
    if (!isFilterEmpty(this.filter) && !validate(this.filter)) {
      return false;
    }

    this.appliedFilter = this.cloneFilterForApply(this.filter);
    this.pagingFilter.offset = 0;
    resetDirty(this.filter);
    return true;
  }

  @bound
  @watchBusy
  async onDownloadClick(item: DistributorIssuedPaymentRequestResponse) {
    if (this.distributorId) {
      try {
        const response = await this.repository.distributorPaymentRequestReportDownload(this.distributorId, item.id);
        download(response.data, `${item.id}.xlsx`);
      } catch (e) {
        console.error(e);
      }
    }
  }

  @bound
  @watchBusy
  async onReportClick(distributorId: string) {
    try {
      const response: any = await this.repository.distributorPaymentRequestReports(distributorId, this.appliedFilter);
      download(response.data, `paymentRequestReport-${this.distributorId}.xlsx`);
    } catch (e) {
      console.error(e);
    }
  }

  @computed
  get distributorOptions() {
    return this.optionsService.entityOptions(Distributor);
  }

  get navigationParams() {
    return {
      ...serializeFilters(this.appliedFilter, false),
      distributorId: this.distributorId,
      offset: this.pagingFilter.offset,
    };
  }

  async navigate(subPath: string | undefined, params: any): Promise<void> {
    if (params?.distributorId) {
      this.filter.distributorId = params.distributorId;
      this.applyFilter();
    }

    return super.navigate(subPath, params);
  }

  getPaymentRequestStatus({ status, dueDate, postingDate }: DistributorIssuedPaymentRequestResponse) {
    const statusText = this.localizationService.translateEnum("distributorPaymentRequestStatus", status);
    if (dueDate > new Date()) {
      return statusText;
    }
    if (postingDate && postingDate < dueDate) {
      return statusText;
    }

    switch (status) {
      case DistributorsPaymentRequestStatus.AfterDueDate:
      case DistributorsPaymentRequestStatus.PaidAfterDue:
      case DistributorsPaymentRequestStatus.PartiallyPaid: {
        const toCheck = postingDate ?? new Date();
        const days = (toCheck.getTime() - dueDate.getTime()) / (1000 * 3600 * 24);
        return `${statusText} (${Math.floor(days)}d)`;
      }
      default:
        return statusText;
    }
  }

  translatePaymentRequestType(distributorPaymentRequestType: DistributorPaymentRequestType) {
    return this.localizationService.translateEnum("distributorPaymentRequestType", distributorPaymentRequestType);
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.timeCharges, `distributorPaymentRequests.${code}`, size);
  }
}
